import { useCookies } from 'react-cookie'
import { trackAddShippingInfo, trackAddToCart } from '@/lib/tracking'
import type { CheckoutModelInput } from '@/service-api/types.generated'
import { useAdditionalInfo } from '@/stores/useAdditionalInfoStore'
import { useBillingAddress, useDeliveryAddress } from '@/stores/useAddressStore'
import { useDeliveryDateContainer, usePickupDateBag, usePickupDateContainer } from '@/stores/useDateStore'
import { useCoupon } from '@/stores/useGlobalStore'
import { isAddressInDeliveryArea } from '@/lib/freight'

import { checkout } from '@/lib/checkout'
import { InvoicePayment } from './invoice-payment'
import { useBasket } from './index'

import { BasketItems } from './basket-items'
import { BasketPriceSummary } from './basket-price-summary'
import { Button } from '@/app/[locale]/(ui)/button'
import { NetsPayment } from '@/app/[locale]/(basket)/nets-payment'
import { ArrowRight } from 'lucide-react'
import { usePathname, useRouter } from '@/i18n/navigation'
import { useTranslations } from 'next-intl'

export const Basket = ({ withPayment }: { withPayment?: 'invoice' | 'nets' }) => {
  const { push } = useRouter()
  const pathname = usePathname()
  const t = useTranslations()
  const deliveryAddress = useDeliveryAddress()
  const billingAddress = useBillingAddress()
  const basket = useBasket()

  const pickupDateBag = usePickupDateBag()
  const pickupDateContainer = usePickupDateContainer()
  const deliveryDateContainer = useDeliveryDateContainer()
  const coupon = useCoupon()
  const additionalInfo = useAdditionalInfo()
  const [cookies, setCookie] = useCookies(['user', 'additionalInfo'])
  const { user } = cookies

  const checkoutModel: CheckoutModelInput = checkout({
    basket,
    user,
    additionalInfo,
    pickupDateBag: pickupDateBag || '',
    pickupDateContainer: pickupDateContainer || '',
    deliveryDateContainer: deliveryDateContainer || '',
    deliveryAddress,
    billingAddress,
    discountCode: coupon || '',
    discountPercent:
      basket?.total && basket.total.gross
        ? (basket.total.discount * 100) / (basket.total.gross + basket.total.discount)
        : 0,
  })

  const isOnHentestedPage = pathname === '/hentested'
  const hasDeliveryAddress = !!deliveryAddress
  const isValidDeliveryAddress = deliveryAddress
    ? isAddressInDeliveryArea(deliveryAddress.kommunenummer, deliveryAddress.postnummer)
    : false

  const cartHasContainer = basket?.cart?.some((item) => item.path?.includes('container'))
  const cartHasBag = basket?.cart?.some((item) => item.path?.includes('isekk'))
  const cartHasFreight = basket?.cart?.some((item) => item.sku?.toLowerCase().includes('frakt'))
  // Cart is empty when it contains a singular freight product
  const isCartEmpty = !basket?.cart || basket.cart.length === 0 || (basket.cart.length === 1 && cartHasFreight)

  // Check if freight items have valid pricing and basket is not updating
  const freightItemsHavePrice =
    !cartHasFreight ||
    (basket.status === 'ready' &&
      basket?.cart?.every(
        (item) =>
          !item.sku?.toLowerCase().includes('frakt') || (item.price && item.price.gross && item.price.gross > 0),
      ))

  const isBagDeliveryInfoValid = !cartHasBag || (cartHasBag && pickupDateBag !== 'null')
  const isContainerDeliveryInfoValid =
    !cartHasContainer || (cartHasContainer && pickupDateContainer !== 'null' && deliveryDateContainer !== 'null')
  const isFormFilled = isValidDeliveryAddress && isBagDeliveryInfoValid && isContainerDeliveryInfoValid

  // Determine button text and destination based on flow type
  const buttonText = basket.isCompany ? t('Next') : isOnHentestedPage ? t('To_Payment') : t('Next')
  const buttonDestination = basket.isCompany ? '/bedrift-info' : isOnHentestedPage ? '/kunde-info' : '/hentested'
  const isButtonDisabled = basket.isCompany
    ? false // No validation needed for company baskets
    : isOnHentestedPage &&
      (!hasDeliveryAddress ||
        !isValidDeliveryAddress ||
        !isFormFilled ||
        !freightItemsHavePrice ||
        basket.status === 'server-basket-is-stale' ||
        basket.status === 'server-update-failed')

  const onNextClick = () => {
    trackAddToCart({ cart: basket?.cart })

    if (isOnHentestedPage && isFormFilled) {
      setCookie('additionalInfo', additionalInfo, {
        path: '/',
        maxAge: 3600,
      })

      trackAddShippingInfo()
    }
    push(buttonDestination)
  }

  return (
    <div className="flex max-w-[600px] flex-col gap-3 overflow-hidden rounded-md border border-black bg-white p-4">
      {isCartEmpty ? (
        <span className="w-full p-8 text-center">{t('No_orders')}</span>
      ) : (
        <>
          <BasketItems data-testid="cart-items-list" />
          {!basket.isCompany && <BasketPriceSummary />}
          {withPayment === 'nets' && <NetsPayment checkoutModel={checkoutModel} />}
          {withPayment === 'invoice' && <InvoicePayment />}
          {!withPayment && (
            <Button variant="primary" disabled={isButtonDisabled} onClick={onNextClick}>
              {buttonText} <ArrowRight size={20} />
            </Button>
          )}
        </>
      )}
    </div>
  )
}
